import { FilterOutlined } from '@ant-design/icons';
import { Button, Cascader, ConfigProvider, Divider, Spin } from 'antd';
import React, { memo, useContext, useEffect, useRef, useState } from 'react';

import useDispatchSetterStore from '../../hooks/useDispatchSetterStore';
import useDynamicSelector from '../../hooks/useDynamicSelector';
import { SmxPropsContext } from '../../providers/propsProvider';
import { getSetterDynamicListItem, IDynamicListState } from '../../store/slices/dynamicListSlice';
import { ListItemType } from '../../types';
import LoaderListItem from './LoaderListItem';

interface IDynamicListItemCascader {
    fieldName: string;
    filters: string[];
    indexUpdate?: number;
}

interface Option {
    value: string | number;
    label: string;
    children?: Option[];
    disableCheckbox?: boolean;
}

const DynamicListItemCascader: React.FC<IDynamicListItemCascader> = ({ fieldName, filters }) => {
    const contextProps = useContext(SmxPropsContext);
    const setterListItem = useDispatchSetterStore<IDynamicListState<any>>(
        getSetterDynamicListItem(contextProps.nameList + contextProps.prefixStore),
    );
    const originalItems = useDynamicSelector('dynamicList', 'originalItems');

    const pageSize = useDynamicSelector('dynamicList', 'pageSize');
    const sorter = useDynamicSelector('dynamicList', 'sorter');

    const [options, setOptions] = useState<Option[]>([]);
    const [isOpenCascader, setIsOpenCascader] = useState(false);
    const [spinner, setSpinner] = useState(false);
    const [listItem, setListItem] = useState<ListItemType[]>([]);
    const [dataFromServer, setDataFromServer] = useState<{
        rows: ListItemType[];
        count: number;
        connectionId: string;
    }>({ rows: [], count: 0, connectionId: '' });
    const refListItemIds = useRef<number[]>([]);
    const dataFromServerRef = useRef<{
        rows: ListItemType[];
        count: number;
        connectionId: string;
    }>({ rows: [], count: 0, connectionId: '' });

    useEffect(() => {
        (async () => {
            setSpinner(true);

            const newListItem: ListItemType[] = (dataFromServer.rows || []).map((row) => ({
                ...row,
                id: +(row as any).key,
            }));
            refListItemIds.current = [];

            const [firstNameList] = filters;

            const newOptions = [];

            const createOption = (item: ListItemType): Option => {
                refListItemIds.current.push(item.id);

                return {
                    label: item.name,
                    value: item.id,
                    children: item.child
                        .split(',')
                        .filter((listItemId) => +listItemId)
                        .map((listItemId) => newListItem.find((subItem) => subItem.id === +listItemId))
                        .filter((item) => item)
                        .map((item) => createOption(item!)),
                };
            };

            for (const item of newListItem) {
                if (item.type !== firstNameList) {
                    continue;
                }

                newOptions.push(createOption(item));
            }

            setSpinner(false);
            setOptions(newOptions);
            setListItem(newListItem);
        })();
    }, [dataFromServer]);

    const onChange = (arraySetsValue: (string | number)[][]) => {
        const countFilter = filters.length;

        const listFilteredId: number[] = [];
        for (const oneSetOfValue of arraySetsValue) {
            if (oneSetOfValue.length === countFilter) {
                listFilteredId.push(oneSetOfValue[countFilter - 1] as number);
            } else {
                const [firstId] = oneSetOfValue;
                const groupItem = listItem.find((item) => item.id === firstId);

                if (groupItem) {
                    const listChildId = groupItem.child
                        .split(',')
                        .filter((id) => refListItemIds.current.includes(+id))
                        .map((id) => +id);
                    listFilteredId.push(...listChildId);
                }
            }
        }

        if (arraySetsValue.length) {
            setterListItem({
                items: originalItems.filter((item) => listFilteredId.includes(item[fieldName])),
            });
        } else {
            setterListItem({ items: [...originalItems] });
        }
    };

    const popupRender = (menu: React.ReactElement) => {
        return (
            <Spin tip="Loading" size="small" spinning={spinner}>
                {menu}
                <Divider style={{ margin: 0 }} />
                <div
                    style={{ padding: 8 }}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <Button
                        type={'default'}
                        onClick={() => {
                            setIsOpenCascader(false);
                        }}
                    >
                        Close
                    </Button>
                </div>
            </Spin>
        );
    };

    return (
        <div
            onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            style={{ overflow: 'scroll' }}
        >
            {filters.map((filter) => (
                <LoaderListItem
                    key={filter}
                    onUpdateData={(rows: any[], count: number, connectionId: string) => {
                        const listIdRow = rows.map((row) => row.id);
                        const newRows = [
                            ...dataFromServerRef.current.rows.filter((row) => !listIdRow.includes(row.id)),
                            ...rows,
                        ];

                        dataFromServerRef.current = {
                            rows: newRows,
                            count,
                            connectionId,
                        };

                        setDataFromServer(dataFromServerRef.current);
                    }}
                    varRequest={{
                        type: filter,
                        first: pageSize,
                        order_by: sorter,
                    }}
                />
            ))}
            <FilterOutlined style={{ padding: '0 5px', fontSize: 16 }} />
            <ConfigProvider
                theme={{
                    components: {
                        Cascader: {
                            dropdownHeight: 150,
                        },
                    },
                }}
            >
                <Cascader
                    style={{ width: 'calc(100% - 30px)' }}
                    options={options}
                    onChange={onChange}
                    multiple
                    open={isOpenCascader}
                    maxTagCount="responsive"
                    onOpenChange={(visibleKey) => setIsOpenCascader(visibleKey)}
                    popupRender={popupRender}
                />
            </ConfigProvider>
        </div>
    );
};

export default memo(DynamicListItemCascader);
